// src/receptionist/screens/Overview.tsx
//
// The console's landing page (#4847, BE ourteam-backend#6718): how the
// receptionist is doing and what needs the team, before anything else.
// Built from the same pieces as Oura's staff Overview (BusinessOverview):
// a row of tiles, then a wide card beside a stack of smaller ones.
//
//   Tiles:   waiting on your team · booking requests · conversations · handed to your team
//   Left:    Needs your decision (the pending queue), Today (confirmed appointments)
//   Right:   Setup (only while unfinished), This week (what the receptionist did on its own)
//
// Every number comes from GET .../overview, which counts the way the Bookings
// queue does, so a tile and the Bookings tab never disagree.

import { useCallback, useEffect, useRef, useState } from "react";
import {
    AlertTriangleIcon,
    CalendarCheckIcon,
    CalendarClockIcon,
    ClipboardCheckIcon,
    ClockIcon,
    MessageSquareTextIcon,
    SparklesIcon,
    UsersRoundIcon,
} from "lucide-react";
import {
    ArrowLink,
    Bento,
    BentoStack,
    Dot,
    FoldCard,
    FoldScope,
    InnerStat,
    ListRow,
    Steps,
    Tile,
    Tiles,
    TYPE,
    type StepItem,
} from "@oura/ui";
import { cn } from "@oura/lib/cn";
import {
    getOverview, getStaffChannel, getWhatsappState, listBookings,
    type ReceptionistOverview, type StaffChannelState, type WhatsappState,
} from "@service/receptionist";
import type { Booking } from "../bookings";
import { MERCHANT_TZ, slotWhen } from "../bookings";
import { PageHeader, Card, CardBody, Button, EmptyState } from "../components/kit";
import { SETUP_STEPS, statusOf, type SetupStepKey } from "./Setup";

const PENDING_SHOWN = 5;

/** "2 days", "3 hours", "5 min". */
function age(iso: string | null | undefined, now = Date.now()): string | null {
    if (!iso) return null;
    const mins = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
    if (Number.isNaN(mins)) return null;
    if (mins < 60) return `${mins} min`;
    const hours = Math.round(mins / 60);
    if (hours < 48) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
    const days = Math.round(hours / 24);
    return `${days} days`;
}

/** "+3 on the week before", "2 fewer than the week before", "same as the week before". */
export function weekDelta(count: number, previous: number): string {
    const d = count - previous;
    if (d === 0) return "Same as the week before";
    return d > 0 ? `${d} more than the week before` : `${-d} fewer than the week before`;
}

/** "11:00 AM" in the merchant's time zone. */
function timeOf(iso: string | null): string {
    if (!iso) return "Time to confirm";
    return new Intl.DateTimeFormat("en-MY", { timeZone: MERCHANT_TZ, hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(iso));
}

type Load<T> = { kind: "loading" } | { kind: "error" } | { kind: "ready"; data: T };

export function OverviewScreen({
    merchantId,
    hrefs,
}: {
    merchantId: string | null;
    hrefs: {
        bookings: string;
        /** One booking open in the Bookings drawer. */
        booking: (id: string) => string;
        setup: (step?: SetupStepKey) => string;
        followUps: string;
    };
}) {
    const [overview, setOverview] = useState<Load<ReceptionistOverview>>({ kind: "loading" });
    const [pending, setPending] = useState<Load<Booking[]>>({ kind: "loading" });
    /** What the Setup menu reads, so the card names and counts the steps exactly as Setup does. */
    const [setup, setSetup] = useState<{ wa: WhatsappState | null; channel: StaffChannelState | null } | null>(null);

    /** Every load, from the effect or Try again, takes a number; only the newest one's answers land (Jack, #4848). */
    const loadSeq = useRef(0);
    const load = useCallback(() => {
        if (!merchantId) return;
        const request = ++loadSeq.current;
        const isCurrent = () => request === loadSeq.current;
        setOverview({ kind: "loading" });
        setPending({ kind: "loading" });
        setSetup(null);
        getOverview(merchantId).then(
            (data) => isCurrent() && setOverview({ kind: "ready", data }),
            () => isCurrent() && setOverview({ kind: "error" }),
        );
        listBookings(merchantId, { status: "pending", page: 1, limit: PENDING_SHOWN }).then(
            (page) => isCurrent() && setPending({ kind: "ready", data: page.bookings }),
            () => isCurrent() && setPending({ kind: "error" }),
        );
        // A setup card that fails to read is left out rather than shown as an error.
        Promise.all([
            getWhatsappState(merchantId).catch(() => null),
            getStaffChannel(merchantId).catch(() => null),
        ]).then(([wa, channel]) => isCurrent() && setSetup(wa ? { wa, channel } : null));
    }, [merchantId]);

    useEffect(() => {
        load();
        return () => { loadSeq.current += 1; };
    }, [load]);

    if (!merchantId) {
        return (
            <Card>
                <CardBody>
                    <EmptyState icon={AlertTriangleIcon} title="No receptionist is linked to this account" />
                </CardBody>
            </Card>
        );
    }

    // A failed read is the error alone: no tile below it may show a 0 it never read (Jack, #4848).
    if (overview.kind === "error") {
        return (
            <div className="space-y-5">
                <PageHeader title="Overview" subtitle="How your receptionist is doing, and what needs your team." />
                <Card>
                    <CardBody>
                        <EmptyState
                            icon={AlertTriangleIcon}
                            title="The overview could not be read just now"
                            action={<Button onClick={() => load()}>Try again</Button>}
                        />
                    </CardBody>
                </Card>
            </div>
        );
    }

    const o = overview.kind === "ready" ? overview.data : null;
    const loading = overview.kind === "loading";
    // The Setup menu's own steps and facts: required ones count, Learn is optional.
    const steps = setup ? SETUP_STEPS.map((s) => ({ ...s, status: statusOf(s.key, setup.wa, setup.channel) })) : [];
    const required = steps.filter((s) => !s.optional);
    // A step whose source could not be read is unknown, not open: the card waits for every required
    // step to be known before it counts or sends anyone anywhere, as Setup's header does (Jack, #4850).
    const known = required.every((s) => s.status !== null);
    const doneCount = required.filter((s) => s.status?.done).length;
    const firstOpen = known ? required.find((s) => !s.status?.done)?.key : undefined;

    return (
        <FoldScope value="receptionist-overview">
            <div className="space-y-5">
                <PageHeader
                    title="Overview"
                    subtitle="How your receptionist is doing, and what needs your team."
                    status={o ? `The last ${o.windowDays} days, against the ${o.windowDays} before` : undefined}
                />

                <Tiles cols={4}>
                    <Tile
                        label="Waiting for your team"
                        icon={{ icon: ClockIcon, area: "asks" }}
                        loading={loading}
                        value={o?.pending.count ?? 0}
                        sub={o ? (o.pending.count === 0 ? "Nothing waiting" : `Oldest waiting ${age(o.pending.oldestAt) ?? "a while"}`) : undefined}
                        to={`${hrefs.bookings}?status=pending`}
                        ariaLabel="Open the pending booking requests"
                    />
                    <Tile
                        label="Booking requests"
                        icon={{ icon: CalendarCheckIcon, area: "dates" }}
                        loading={loading}
                        value={o?.bookings.count ?? 0}
                        sub={o ? weekDelta(o.bookings.count, o.bookings.previous) : undefined}
                        to={hrefs.bookings}
                    />
                    <Tile
                        label="Conversations"
                        icon={{ icon: MessageSquareTextIcon, area: "ai" }}
                        loading={loading}
                        value={o?.conversations.count ?? 0}
                        sub={o ? `${o.conversations.aiReplies} replies by your receptionist` : undefined}
                    />
                    <Tile
                        label="Handed to your team"
                        icon={{ icon: UsersRoundIcon, area: "people" }}
                        loading={loading}
                        value={o?.handedToTeam.count ?? 0}
                        sub={o ? weekDelta(o.handedToTeam.count, o.handedToTeam.previous) : undefined}
                    />
                </Tiles>

                <Bento layout="c21">
                    <BentoStack>
                        <FoldCard
                            id="needs-decision"
                            title="Needs your decision"
                            icon={{ icon: ClockIcon, area: "asks" }}
                            right={o ? <span className="tabular-nums">{o.pending.count} pending</span> : undefined}
                            alwaysOpen
                        >
                            {pending.kind === "loading" && <p className={cn(TYPE.tc, "m-0 py-2 text-gray-500")}>Loading…</p>}
                            {pending.kind === "error" && <p className={cn(TYPE.tc, "m-0 py-2 text-gray-500")}>The pending requests could not be read.</p>}
                            {pending.kind === "ready" && pending.data.length === 0 && (
                                <p className={cn(TYPE.tc, "m-0 py-2 text-gray-500")}>Nothing waiting. New requests land here and in your staff WhatsApp.</p>
                            )}
                            {pending.kind === "ready" && pending.data.map((b) => {
                                const when = slotWhen(b);
                                const waited = age(b.createdAt);
                                return (
                                    <ListRow
                                        key={b.id}
                                        icon={<Dot tone="amber" />}
                                        title={`${b.customer}${b.service ? ` · ${b.service}` : ""}`}
                                        sub={when ? `${when.date}, ${when.time}` : b.slot ? `Customer said: ${b.slot}` : "No time yet"}
                                        meta={waited ? `Waiting ${waited}` : undefined}
                                        to={hrefs.booking(b.id)}
                                        className="py-2.5"
                                    />
                                );
                            })}
                            {o && o.pending.count > PENDING_SHOWN && (
                                <div className="pt-2">
                                    <ArrowLink to={`${hrefs.bookings}?status=pending`}>All {o.pending.count} pending requests</ArrowLink>
                                </div>
                            )}
                        </FoldCard>

                        <FoldCard
                            id="today"
                            title="Today"
                            icon={{ icon: CalendarClockIcon, area: "dates" }}
                            right={o ? <span className="tabular-nums">{o.today.length} confirmed</span> : undefined}
                            alwaysOpen
                        >
                            {o && o.today.length === 0 && (
                                <p className={cn(TYPE.tc, "m-0 py-2 text-gray-500")}>No confirmed appointments today.</p>
                            )}
                            {o?.today.map((t) => (
                                <ListRow
                                    key={t.id}
                                    icon={<Dot tone="green" />}
                                    title={`${timeOf(t.slotAt)} · ${t.customer}`}
                                    sub={t.service || undefined}
                                    to={hrefs.booking(t.id)}
                                    className="py-2.5"
                                />
                            ))}
                        </FoldCard>
                    </BentoStack>

                    <BentoStack>
                        {setup && firstOpen && (
                            <FoldCard
                                id="setup"
                                title="Finish setup"
                                icon={{ icon: ClipboardCheckIcon, area: "setup" }}
                                right={<span className="tabular-nums">{doneCount} of {required.length} done</span>}
                                alwaysOpen
                            >
                                <Steps
                                    items={steps.map<StepItem>((s) => ({
                                        key: s.key,
                                        title: s.title,
                                        meta: s.status?.fact,
                                        state: s.status?.done ? "done" : s.key === firstOpen ? "now" : s.optional ? "later" : "next",
                                    }))}
                                />
                                <div className="pt-3">
                                    <ArrowLink to={hrefs.setup(firstOpen)}>Continue setup</ArrowLink>
                                </div>
                            </FoldCard>
                        )}

                        <FoldCard id="this-week" title="This week" icon={{ icon: SparklesIcon, area: "ai" }} alwaysOpen>
                            {o ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <InnerStat label="Confirmed" value={o.bookings.confirmed} sub={`${o.bookings.declined} declined`} />
                                    <InnerStat label="After hours" value={o.bookings.afterHours} sub="Requests taken while closed" />
                                    <InnerStat label="Follow-ups sent" value={o.followUpsSent} sub={<ArrowLink to={hrefs.followUps}>Follow-ups</ArrowLink>} />
                                    <InnerStat label="Went quiet, nudged" value={o.nudged} sub="Customers reminded mid-booking" />
                                </div>
                            ) : (
                                <p className={cn(TYPE.tc, "m-0 py-2 text-gray-500")}>{loading ? "Loading…" : "Not available just now."}</p>
                            )}
                        </FoldCard>
                    </BentoStack>
                </Bento>
            </div>
        </FoldScope>
    );
}
